/**
 * Breadcrumbs — Folder Navigation Trail
 *
 * Renders a clickable breadcrumb path matching the Figma design:
 *   Home > ... > Manchester > Angel Gardens
 *
 * Rules:
 * - "Home" is always the first segment (root of file management).
 * - If the path has 3+ items, middle segments collapse into "...".
 * - Each visible segment is clickable — navigates to that folder.
 * - Only shown when inside a folder (not at root level).
 */

import type { KBFolderPathItem } from "../types/fileManagement.types";

interface BreadcrumbsProps {
  /** Ancestor chain from API; last item is the current folder. */
  path: KBFolderPathItem[];
  /** Called when user clicks "Home" — goes to root file listing. */
  onNavigateToRoot: () => void;
  /** Called when user clicks a specific folder in the breadcrumb trail. */
  onNavigateToFolder: (folderIndex: number) => void;
}

const Breadcrumbs = ({
  path,
  onNavigateToRoot,
  onNavigateToFolder,
}: BreadcrumbsProps) => {
  if (path.length === 0) return null;

  /**
   * Build the visible breadcrumb segments.
   *
   * For deep paths (3+ folders), we collapse the middle into "...":
   *   [A, B, C, D] → Home > ... > C > D
   *   [A, B]       → Home > A > B
   *   [A]          → Home > A
   */
  const getVisibleSegments = () => {
    if (path.length <= 2) {
      return path.map((item, index) => ({
        ...item,
        originalIndex: index,
        isEllipsis: false,
      }));
    }

    const parentFolder = path[path.length - 2];
    const currentFolder = path[path.length - 1];

    return [
      { _id: "ellipsis", name: "...", originalIndex: -1, isEllipsis: true },
      { ...parentFolder, originalIndex: path.length - 2, isEllipsis: false },
      { ...currentFolder, originalIndex: path.length - 1, isEllipsis: false },
    ];
  };

  const segments = getVisibleSegments();

  return (
    <nav aria-label="Folder navigation" className="flex items-center">
      <ol className="flex items-center gap-1 text-md">
        {/* "Home" — always first, goes to root */}
        <li>
          <button
            type="button"
            onClick={onNavigateToRoot}
            className="text-verba-blue dark:text-verba-blue-dk font-medium hover:underline cursor-pointer"
          >
            Home
          </button>
        </li>

        {/* Folder segments */}
        {segments.map((segment) => {
          const isLast = segment.originalIndex === path.length - 1;

          return (
            <li key={segment._id} className="flex items-center">
              <span className="text-dark-gray dark:text-dark-gray-dk mx-1">
                &gt;
              </span>

              {segment.isEllipsis ? (
                <span className="text-dark-gray dark:text-dark-gray-dk font-medium">
                  ...
                </span>
              ) : isLast ? (
                <span className="text-heading-text dark:text-heading-text-dk font-medium truncate max-w-[200px]">
                  {segment.name}
                </span>
              ) : (
                <button
                  type="button"
                  onClick={() => onNavigateToFolder(segment.originalIndex)}
                  className="text-verba-blue dark:text-verba-blue-dk font-medium hover:underline cursor-pointer truncate max-w-[200px]"
                >
                  {segment.name}
                </button>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
};

export default Breadcrumbs;
